import { ExternalLink, FileText, Download, Play, GraduationCap, Users, Award } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import RadialSectionNav from '../../page_assets/RadialSectionNav';
import SectionNavBar from '../../page_assets/SectionNavBar';

// Clearance left above a section when jumping to it, so its first row doesn't land
// under the fixed header.
const SCROLL_MARGIN = 96;

// How far down the viewport the line sits that decides which section you are in. A
// third of the way down rather than at the top edge: the section you are reading is
// the one filling the screen, not the one whose last card is still scrolling out.
const ACTIVE_LINE = 0.35;

function LazyVideo({ media, title }) {
  const [shouldLoad, setShouldLoad] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
        }
      },
      {
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const handleVideoLoad = () => {
    if (videoRef.current) {
      videoRef.current.preload = 'metadata';
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {shouldLoad ? (
        <video 
          ref={videoRef}
          src={media.url} 
          controls
          loop
          muted
          preload="none"
          className="w-full h-48 object-cover rounded border border-yellow-500"
          poster={media.thumbnail}
          onLoadedData={handleVideoLoad}
        >
          Your browser does not support the video tag.
        </video>
      ) : (
        <div className="w-full h-48 bg-neutral-800 rounded border border-yellow-500 flex items-center justify-center relative">
          {media.thumbnail ? (
            <img 
              src={media.thumbnail} 
              alt={media.alt || title}
              className="w-full h-48 object-cover rounded border border-yellow-500"
            />
          ) : (
            <div className="text-neutral-400 flex flex-col items-center">
              <Play className="w-8 h-8 mb-2" />
              <span className="text-sm">Video will load when visible</span>
            </div>
          )}
          <div className="absolute top-2 left-2 bg-black bg-opacity-50 rounded px-2 py-1">
            <Play className="w-4 h-4 text-white" />
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectMedia({ media, title }) {
  if (!media) return null;

  if (media.type === 'video') {
    return <LazyVideo media={media} title={title} />;
  }

  if (media.type === 'image') {
    return (
      <img 
        src={media.url} 
        alt={media.alt || title}
        className="w-full h-48 object-cover rounded border border-neutral-700"
        loading="lazy"
      />
    );
  }

  return null;
}

function ResearchInfo({ research }) {
  if (!research) return null;

  return (
    <div className="mb-4 bg-neutral-800 rounded-lg p-3 border border-neutral-700">
      <h4 className="text-sm font-semibold text-yellow-400 mb-2 flex items-center gap-2">
        <GraduationCap className="w-4 h-4" />
        Research Details
      </h4>
      <div className="space-y-2 text-xs">
        {research.university && (
          <div className="flex items-center gap-2">
            <span className="text-neutral-400 font-medium">University:</span>
            <span className="text-yellow-100">{research.university}</span>
          </div>
        )}
        {research.advisor && (
          <div className="flex items-center gap-2">
            <Users className="w-3 h-3 text-neutral-400" />
            <span className="text-neutral-400 font-medium">Advisor:</span>
            <span className="text-yellow-100">{research.advisor}</span>
          </div>
        )}
        {research.funding && (
          <div>
          <p className="text-neutral-400 font-medium">Funded by:</p>
          <p className="text-yellow-100">{research.funding}</p>
        </div>
        )}
        {research.presentations && research.presentations.length > 0 && (
          <div className="flex items-start gap-2">
            <Award className="w-3 h-3 text-neutral-400 mt-0.5" />
            <div>
              <span className="text-neutral-400 font-medium">Presented at:</span>
              <ul className="text-yellow-100 ml-2">
                {research.presentations.map((presentation, index) => (
                  <li key={index} className="list-disc list-inside">{presentation}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {research.status && (
          <div className="flex items-center gap-2">
            <span className="text-neutral-400 font-medium">Status:</span>
            <span className={`text-sm px-2 py-1 rounded ${
              research.status.includes('Coming Soon') || research.status.includes('In Progress')
                ? 'bg-yellow-600 text-yellow-100'
                : research.status.includes('Published') || research.status.includes('Submitted')
                ? 'bg-green-600 text-green-100'
                : 'bg-neutral-600 text-neutral-100'
            }`}>
              {research.status}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentLinks({ documents }) {
  if (!documents || documents.length === 0) return null;

  return (
    <div className="mb-4">
      <h4 className="text-sm font-semibold text-yellow-400 mb-2">Supporting Documents:</h4>
      <div className="flex flex-wrap gap-2">
        {documents.map((doc, index) => {
          if (!doc.url || doc.url === "" || doc.description?.toLowerCase().includes('coming soon')) {
            return (
              <div
                key={index}
                className="inline-flex items-center gap-1 bg-neutral-600 text-neutral-300 px-3 py-1 rounded text-sm cursor-not-allowed opacity-75"
                title={doc.description || doc.name}
              >
                <FileText className="w-3 h-3" />
                <span className="text-xs">{doc.name}</span>
                <span className="text-xs italic ml-1">(Coming Soon)</span>
              </div>
            );
          }

          return (
            <a
              key={index}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 bg-neutral-700 hover:bg-neutral-600 text-neutral-200 hover:text-white px-3 py-1 rounded text-sm transition-colors"
              title={doc.description || doc.name}
            >
              {doc.type === 'pdf' && <FileText className="w-3 h-3" />}
              {doc.type === 'download' && <Download className="w-3 h-3" />}
              {doc.type === 'external' && <ExternalLink className="w-3 h-3" />}
              <span className="text-xs">{doc.name}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One project block. A card with a link is the whole clickable surface; one without is
 * the same card as a plain div. Each animates itself in as it reaches the screen, the
 * same one-shot reveal the gallery's tiles use.
 */
function ProjectCard({ project, cardRef }) {
  const [revealed, setRevealed] = useState(false);
  const nodeRef = useRef(null);

  // The page wants this same element for section measurement, so its callback is folded
  // into ours — read through a ref at call time because the parent passes a fresh arrow
  // every render, and depending on it directly would make React detach and reattach the
  // ref on every one.
  const parentRef = useRef(cardRef);
  parentRef.current = cardRef;
  const setNode = useCallback((el) => {
    nodeRef.current = el;
    parentRef.current?.(el);
  }, []);

  useEffect(() => {
    const el = nodeRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setRevealed(true);
        observer.disconnect();
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isLink = !!project.link;
  const Wrapper = isLink ? 'a' : 'div';
  const linkProps = isLink
    ? { href: project.link, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Wrapper
      ref={setNode}
      {...linkProps}
      className={`block bg-neutral-900 border border-neutral-700 rounded-lg p-6 h-full ${
        isLink
          ? 'hover:bg-neutral-800 transition-colors cursor-pointer transform hover:scale-[101%] duration-300 hover:shadow-xl'
          : ''
      } ${revealed ? 'animate-tile-in' : 'opacity-0'}`}
    >
      <div className="flex flex-col h-full">
        <div className={`flex flex-col ${project.research ? 'md:min-h-64' : 'md:min-h-44'} mb-5`}>
          <h3 className="text-xl font-bold mb-2">{project.title}</h3>
          <p className="text-yellow-100 font-serif text-sm leading-relaxed flex-grow">{project.description}</p>
        </div>

        <div className="flex-shrink-0 mb-4">
          <ProjectMedia media={project.media} title={project.title} />

          {!project.media && project.image && (
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-48 object-cover rounded border border-neutral-700"
            />
          )}
        </div>

        <div className="flex-grow">
          <ResearchInfo research={project.research} />
          <DocumentLinks documents={project.documents} />
        </div>

        {isLink && (
          <div className="flex justify-end mt-auto pt-4">
            <div className="flex items-center text-yellow-400 text-sm font-medium">
              <ExternalLink className="w-4 h-4 mr-2" />
              Click to view project
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

/**
 * Every project in one continuous grid. The run isn't broken up by headings, so the two
 * navs are the only thing saying where one category ends and the next begins — the wheel
 * on desktop, the row of jumps on a phone.
 *
 * @param projects     the whole run, flattened, in category order
 * @param sections     [{ name, startIndex }] — where each category begins in that run
 * @param accent       palette for the two navs
 * @param radialNav    whether the desktop wheel comes along. Off for a short run — the
 *                     home page's band is six cards, which is less scrolling than the
 *                     wheel saves
 * @param scrollToRef  ref this hands its jump-to-section function back through, so the
 *                     landing's heading can drive it too — where a category starts is
 *                     measured off the cards, so only this component can know it
 */
export default function CSPortfolioComponent({
  projects = [],
  sections = [],
  accent = 'yellow',
  radialNav = true,
  scrollToRef,
}) {
  const gridRef = useRef(null);
  const cardRefs = useRef([]);
  // Document-space y of each section's first card. A ref rather than state because it is
  // rewritten every animation frame of a scroll, and rendering on it would mean a render
  // per frame for a number nothing displays.
  const sectionTops = useRef([]);
  const frame = useRef(null);

  const [activeSection, setActiveSection] = useState(0);
  const [navVisible, setNavVisible] = useState(false);

  const measure = useCallback(() => {
    // The grid lays cards out in source order, so a category's first card is simply the
    // one at its start index — no column arithmetic, unlike the gallery's masonry.
    sectionTops.current = sections.map(({ startIndex }) => {
      const card = cardRefs.current[startIndex];
      return card ? card.getBoundingClientRect().top + window.scrollY : null;
    });

    const line = window.scrollY + window.innerHeight * ACTIVE_LINE;
    let current = 0;
    sectionTops.current.forEach((top, index) => {
      if (top !== null && top <= line) current = index;
    });
    setActiveSection(current);

    // The wheel belongs to the run: it arrives once the grid is well into the screen and
    // leaves again once the run has scrolled off the top. The second half of that matters
    // on the home page, where the run has a whole section under it rather than just the
    // footer — a wheel still pointing into the grid from over the contact screen aims at
    // nothing on it.
    const grid = gridRef.current;
    const gridBox = grid?.getBoundingClientRect();
    setNavVisible(
      !!gridBox && gridBox.top < window.innerHeight * 0.6 && gridBox.bottom > window.innerHeight * 0.4
    );
  }, [sections]);

  const remeasure = useCallback(() => {
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    if (!sections.length) return undefined;

    measure();
    window.addEventListener('scroll', remeasure, { passive: true });
    window.addEventListener('resize', remeasure);

    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
      window.removeEventListener('scroll', remeasure);
      window.removeEventListener('resize', remeasure);
    };
  }, [sections, measure, remeasure]);

  const scrollToSection = useCallback((index) => {
    const top = sectionTops.current[index];
    if (top == null) return;
    window.scrollTo({ top: Math.max(0, top - SCROLL_MARGIN), behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!scrollToRef) return undefined;
    scrollToRef.current = scrollToSection;
    return () => {
      scrollToRef.current = null;
    };
  }, [scrollToRef, scrollToSection]);

  return (
    <div className="text-yellow-400 pt-20 px-4 sm:px-8 pb-4 sm:pb-8">
      {radialNav && sections.length > 0 && (
        <RadialSectionNav
          sections={sections}
          activeIndex={activeSection}
          visible={navVisible}
          onSelect={scrollToSection}
          accent={accent}
        />
      )}

      <div ref={gridRef} className="relative z-10 max-w-6xl mx-auto">
        <SectionNavBar
          sections={sections}
          activeIndex={activeSection}
          onSelect={scrollToSection}
          accent={accent}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {projects.length > 0 ? (
            projects.map((project, index) => (
              <ProjectCard
                key={index}
                project={project}
                cardRef={(el) => {
                  cardRefs.current[index] = el;
                }}
              />
            ))
          ) : (
            <div className="col-span-full text-left text-yellow-100 py-12">
              <p className="text-xl font-serif">Projects coming soon...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
